import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Card, Badge, ProgressBar, Tab, Tabs } from 'react-bootstrap';
import { toast } from 'react-toastify';
import VodService from '../../../services/VodService';
import CaptionManager from '../../components/Common/CaptionManager';

const VideoEditor = ({ show, onHide, asset, onSave }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Video state
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Trim settings
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [isTrimMode, setIsTrimMode] = useState(false);
  const [previewTrim, setPreviewTrim] = useState(false);
  
  // Video effects
  const [effects, setEffects] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    blur: 0,
    grayscale: 0,
    sepia: 0,
    hueRotate: 0,
    invert: 0
  });
  
  // Text overlay
  const [textOverlay, setTextOverlay] = useState({
    enabled: false,
    text: '',
    position: 'bottom-center',
    fontSize: 24,
    color: '#ffffff',
    backgroundColor: 'rgba(0,0,0,0.5)',
    x: 50,
    y: 90
  });
  
  // Audio settings
  const [audioSettings, setAudioSettings] = useState({
    volume: 100,
    muted: false,
    removeAudio: false
  });
  
  // Output settings
  const [outputFormat, setOutputFormat] = useState('mp4');
  const [quality, setQuality] = useState('high');
  const [resolution, setResolution] = useState('original');
  
  // Caption manager modal
  const [showCaptionModal, setShowCaptionModal] = useState(false);

  useEffect(() => {
    if (asset && videoRef.current) {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('streamToken');
      
      if (!token) {
        toast.error('Authentication token not found. Please log in again.');
        return;
      }
      
      const videoUrl = `${API_URL}/vod/stream/${asset.id}?token=${encodeURIComponent(token)}`;
      
      // Reset video state when asset changes
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setTrimStart(0);
      setTrimEnd(0);
      
      // Clear previous source first
      videoRef.current.removeAttribute('src');
      videoRef.current.load();
      
      // Set new source after a brief delay to ensure clean state
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.src = videoUrl;
          videoRef.current.load();
        }
      }, 100);
    }
  }, [asset]);

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const dur = videoRef.current.duration;
      setDuration(dur);
      setTrimEnd(dur);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const time = videoRef.current.currentTime;
      setCurrentTime(time);
      
      // If previewing trim and we hit the end point, pause and reset to start
      if (previewTrim && time >= trimEnd) {
        videoRef.current.pause();
        videoRef.current.currentTime = trimStart;
        setIsPlaying(false);
      }
    }
  };

  const togglePlay = async () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        try {
          // Check if video has a valid source
          if (!videoRef.current.src || videoRef.current.src === '') {
            toast.error('No video source available');
            return;
          }
          
          // Check if video is ready
          if (videoRef.current.readyState < 2) { // HAVE_CURRENT_DATA
            toast.info('Video is still loading, please wait...');
            return;
          }
          
          // If previewing trim, ensure we start from trimStart
          if (previewTrim && videoRef.current.currentTime < trimStart) {
            videoRef.current.currentTime = trimStart;
          }
          
          await videoRef.current.play();
          setIsPlaying(true);
        } catch (error) {
          console.error('Error playing video:', error);
          if (error.name === 'NotSupportedError') {
            toast.error('Video format not supported or no valid source available');
          } else {
            toast.error('Error playing video: ' + error.message);
          }
          setIsPlaying(false);
        }
      }
    }
  };

  const handleSeek = (time) => {
    if (videoRef.current) {
      // If in trim preview mode, constrain seeking to trim range
      if (previewTrim) {
        time = Math.max(trimStart, Math.min(time, trimEnd));
      }
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getFilterString = () => {
    const filters = [];
    
    if (effects.brightness !== 100) {
      filters.push(`brightness=${effects.brightness / 100}`);
    }
    if (effects.contrast !== 100) {
      filters.push(`contrast=${effects.contrast / 100}`);
    }
    if (effects.saturation !== 100) {
      filters.push(`saturation=${effects.saturation / 100}`);
    }
    if (effects.blur > 0) {
      filters.push(`blur=${effects.blur}`);
    }
    if (effects.grayscale > 0) {
      filters.push(`grayscale=${effects.grayscale / 100}`);
    }
    if (effects.sepia > 0) {
      filters.push(`sepia=${effects.sepia / 100}`);
    }
    if (effects.hueRotate !== 0) {
      filters.push(`hue-rotate(${effects.hueRotate}deg)`);
    }
    if (effects.invert > 0) {
      filters.push(`invert(${effects.invert}%)`);
    }
    
    return filters.join(' ');
  };

  const applyVideoStyle = () => {
    const filterString = getFilterString();
    return {
      filter: filterString,
      width: '100%',
      maxHeight: '400px',
      backgroundColor: '#000'
    };
  };

  const setTrimStartAtCurrent = () => {
    if (videoRef.current) {
      const time = videoRef.current.currentTime;
      if (time < trimEnd) {
        setTrimStart(time);
        // Visual feedback - briefly highlight the start marker
        const startMarker = document.querySelector('.trim-start-marker');
        if (startMarker) {
          startMarker.style.transform = 'translate(-50%, -50%) scale(1.3)';
          startMarker.style.boxShadow = '0 0 10px #007bff';
          setTimeout(() => {
            startMarker.style.transform = 'translate(-50%, -50%) scale(1)';
            startMarker.style.boxShadow = 'none';
          }, 300);
        }
        toast.success(`Trim start set to ${formatTime(time)}`);
      } else {
        toast.error('Start point must be before end point');
      }
    }
  };

  const setTrimEndAtCurrent = () => {
    if (videoRef.current) {
      const time = videoRef.current.currentTime;
      if (time > trimStart) {
        setTrimEnd(time);
        // Visual feedback - briefly highlight the end marker
        const endMarker = document.querySelector('.trim-end-marker');
        if (endMarker) {
          endMarker.style.transform = 'translate(-50%, -50%) scale(1.3)';
          endMarker.style.boxShadow = '0 0 10px #dc3545';
          setTimeout(() => {
            endMarker.style.transform = 'translate(-50%, -50%) scale(1)';
            endMarker.style.boxShadow = 'none';
          }, 300);
        }
        toast.success(`Trim end set to ${formatTime(time)}`);
      } else {
        toast.error('End point must be after start point');
      }
    }
  };

  const toggleTrimPreview = () => {
    setPreviewTrim(!previewTrim);
    if (!previewTrim && videoRef.current) {
      // When enabling trim preview, jump to trim start
      videoRef.current.currentTime = trimStart;
      setCurrentTime(trimStart);
      toast.info('Trim preview enabled - video will loop between trim points');
    } else {
      toast.info('Trim preview disabled');
    }
  };

  const handleSave = async () => {
    try {
      setProcessing(true);
      setProgress(0);
      
      const editData = {
        trimStart,
        trimEnd,
        effects,
        textOverlay,
        audioSettings,
        outputFormat,
        quality,
        resolution
      };
      
      const response = await VodService.editVideo(asset.id, editData);
      
      if (response.success) {
        toast.success('Video edited successfully!');
        onSave(response.asset);
        onHide();
      } else {
        toast.error('Failed to edit video');
      }
    } catch (error) {
      console.error('Error editing video:', error);
      // Handle specific error cases
      if (error.response?.status === 503) {
        toast.error('Video editing is temporarily unavailable. Please contact your administrator to enable this feature.');
      } else if (error.response?.data?.error) {
        toast.error('Error: ' + error.response.data.error);
      } else {
        toast.error('Error editing video: ' + error.message);
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = async () => {
    try {
      setProcessing(true);
      setProgress(0);
      
      const editData = {
        trimStart,
        trimEnd,
        effects,
        textOverlay,
        audioSettings,
        outputFormat,
        quality,
        resolution
      };
      
      // First process the video
      const response = await VodService.editVideo(asset.id, editData);
      
      if (response.success && response.downloadUrl) {
        // Construct full download URL with token
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const token = localStorage.getItem('streamToken');
        const fullDownloadUrl = `${API_URL}${response.downloadUrl}?token=${encodeURIComponent(token)}`;
        
        // Trigger download
        const link = document.createElement('a');
        link.href = fullDownloadUrl;
        link.download = `edited_${asset.title}.${outputFormat}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('Video download started!');
      } else {
        toast.error('Failed to prepare video for download');
      }
    } catch (error) {
      console.error('Error downloading video:', error);
      // Handle specific error cases
      if (error.response?.status === 503) {
        toast.error('Video editing is temporarily unavailable. Please contact your administrator to enable this feature.');
      } else if (error.response?.data?.error) {
        toast.error('Error: ' + error.response.data.error);
      } else {
        toast.error('Error downloading video: ' + error.message);
      }
    } finally {
      setProcessing(false);
    }
  };

  const resetEffects = () => {
    setEffects({
      brightness: 100,
      contrast: 100,
      saturation: 100,
      blur: 0,
      grayscale: 0,
      sepia: 0,
      hueRotate: 0,
      invert: 0
    });
  };

  const presetEffects = {
    normal: { brightness: 100, contrast: 100, saturation: 100, blur: 0, grayscale: 0, sepia: 0, hueRotate: 0, invert: 0 },
    vintage: { brightness: 110, contrast: 90, saturation: 80, blur: 0, grayscale: 0, sepia: 60, hueRotate: 0, invert: 0 },
    blackWhite: { brightness: 100, contrast: 120, saturation: 0, blur: 0, grayscale: 100, sepia: 0, hueRotate: 0, invert: 0 },
    cinematic: { brightness: 90, contrast: 110, saturation: 120, blur: 0, grayscale: 0, sepia: 10, hueRotate: 0, invert: 0 },
    warm: { brightness: 105, contrast: 100, saturation: 110, blur: 0, grayscale: 0, sepia: 30, hueRotate: 10, invert: 0 },
    cool: { brightness: 100, contrast: 105, saturation: 90, blur: 0, grayscale: 0, sepia: 0, hueRotate: 180, invert: 0 }
  };

  const applyPreset = (presetName) => {
    setEffects(presetEffects[presetName]);
  };

  if (!asset) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static" keyboard={!processing}>
      <Modal.Header closeButton={!processing}>
        <Modal.Title>
          <i className="flaticon-video-editor mr-2"></i>
          Video Editor: {asset.title}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {processing && (
          <div className="mb-3">
            <Badge variant="primary" className="mb-2">Processing Video...</Badge>
            <ProgressBar animated now={progress} label={`${progress}%`} />
          </div>
        )}
        
        <Row>
          {/* Video Preview */}
          <Col md={8}>
                <Card className="mb-3">
                  <Card.Body className="p-0">
                    <div className="video-container" style={{ backgroundColor: '#000', position: 'relative' }}>
                      {/* Trim Range Overlay */}
                      {isTrimMode && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            zIndex: 10
                          }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              left: `${(trimStart / duration) * 100}%`,
                              width: `${((trimEnd - trimStart) / duration) * 100}%`,
                              height: '100%',
                              backgroundColor: previewTrim ? '#28a745' : '#ffc107'
                            }}
                          />
                        </div>
                      )}
                      <video
                        ref={videoRef}
                        style={applyVideoStyle()}
                        onLoadedMetadata={handleLoadedMetadata}
                        onTimeUpdate={handleTimeUpdate}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        onError={(e) => {
                          console.error('Video error:', e);
                          const video = e.target;
                          let errorMsg = 'Error loading video. ';
                          
                          if (video.error) {
                            switch (video.error.code) {
                              case 1:
                                errorMsg += 'Video loading aborted.';
                                break;
                              case 2:
                                errorMsg += 'Network error - please check your connection.';
                                break;
                              case 3:
                                errorMsg += 'Video decoding error - format may not be supported.';
                                break;
                              case 4:
                                errorMsg += 'Video format not supported.';
                                break;
                              default:
                                errorMsg += 'Unknown error occurred.';
                            }
                          }
                          
                          toast.error(errorMsg);
                          setIsPlaying(false);
                        }}
                        controls={false}
                        playsInline
                        preload="metadata"
                        crossOrigin="anonymous"
                      />

                  
                  {/* Text Overlay Preview */}
                  {textOverlay.enabled && textOverlay.text && (
                    <div
                      style={{
                        position: 'absolute',
                        left: `${textOverlay.x}%`,
                        top: `${textOverlay.y}%`,
                        transform: 'translate(-50%, -50%)',
                        fontSize: `${textOverlay.fontSize}px`,
                        color: textOverlay.color,
                        backgroundColor: textOverlay.backgroundColor,
                        padding: '8px 16px',
                        borderRadius: '4px',
                        pointerEvents: 'none',
                        textAlign: 'center',
                        maxWidth: '80%',
                        wordWrap: 'break-word'
                      }}
                    >
                      {textOverlay.text}
                    </div>
                  )}
                  
                  {/* Play/Pause Overlay */}
                  <div 
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      cursor: 'pointer',
                      opacity: isPlaying ? 0 : 1,
                      transition: 'opacity 0.3s'
                    }}
                    onClick={togglePlay}
                  >
                    <i className="flaticon-play-button" style={{ fontSize: '64px', color: 'rgba(255,255,255,0.8)' }}></i>
                  </div>
                </div>
                
                {/* Timeline Controls */}
                <div className="p-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Button 
                      variant={isPlaying ? "outline-secondary" : "primary"} 
                      size="sm"
                      onClick={togglePlay}
                      disabled={processing}
                    >
                      <i className={`flaticon-${isPlaying ? 'pause' : 'play'}`}></i>
                      {isPlaying ? ' Pause' : ' Play'}
                    </Button>
                    <span className="text-muted">
                      {formatTime(currentTime)} / {formatTime(duration)}
                      {previewTrim && <Badge variant="success" className="ml-2">Trim Preview</Badge>}
                    </span>
                  </div>
                  
                  {/* Scissors Trim Controls */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Label className="mb-0">
                        <i className="flaticon-scissors mr-2"></i>
                        Trim Range: {formatTime(trimStart)} - {formatTime(trimEnd)}
                      </Form.Label>
                      <div>
                        <Button
                          variant={isTrimMode ? "primary" : "outline-secondary"}
                          size="sm"
                          onClick={() => setIsTrimMode(!isTrimMode)}
                          className="mr-2"
                        >
                          <i className="flaticon-scissors mr-1"></i>
                          {isTrimMode ? 'Done' : 'Scissors'}
                        </Button>
                        {isTrimMode && (
                          <Button
                            variant={previewTrim ? "success" : "outline-success"}
                            size="sm"
                            onClick={toggleTrimPreview}
                          >
                            <i className={`flaticon-${previewTrim ? 'visible' : 'invisible'}`}></i>
                            {previewTrim ? 'Stop Preview' : 'Preview Trim'}
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {isTrimMode && (
                      <div className="trim-controls bg-light p-2 rounded mb-2">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={setTrimStartAtCurrent}
                            className="mr-2"
                          >
                            <i className="flaticon-left-arrow mr-1"></i>
                            Set Start ({formatTime(currentTime)})
                          </Button>
                          <span className="text-muted">Current: {formatTime(currentTime)}</span>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={setTrimEndAtCurrent}
                            className="ml-2"
                          >
                            Set End ({formatTime(currentTime)})
                            <i className="flaticon-right-arrow ml-1"></i>
                          </Button>
                        </div>
                        <Form.Text className="text-muted d-block">
                          Navigate to desired position and click "Set Start" or "Set End" to mark trim points.
                          {previewTrim && ' Video will loop between these points.'}
                        </Form.Text>
                      </div>
                    )}
                  </div>
                  
                  {/* Trim Timeline Visualization */}
                  {isTrimMode && (
                    <div className="timeline-container mb-3">
                      <div className="position-relative" style={{ height: '30px', backgroundColor: '#e9ecef', borderRadius: '4px' }}>
                        {/* Full duration bar */}
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            height: '4px',
                            backgroundColor: '#dee2e6'
                          }}
                        />
                        {/* Trim range bar */}
                        <div
                          style={{
                            position: 'absolute',
                            left: `${(trimStart / duration) * 100}%`,
                            width: `${((trimEnd - trimStart) / duration) * 100}%`,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            height: '8px',
                            backgroundColor: previewTrim ? '#28a745' : '#ffc107',
                            borderRadius: '2px'
                          }}
                        />
                        {/* Start marker */}
                        <div
                          className="trim-start-marker"
                          style={{
                            position: 'absolute',
                            left: `${(trimStart / duration) * 100}%`,
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '12px',
                            height: '20px',
                            backgroundColor: '#007bff',
                            borderRadius: '2px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border: '2px solid white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                          }}
                          title={`Start: ${formatTime(trimStart)}`}
                          onClick={() => {
                            if (videoRef.current) {
                              videoRef.current.currentTime = trimStart;
                              setCurrentTime(trimStart);
                            }
                          }}
                        />
                        {/* End marker */}
                        <div
                          className="trim-end-marker"
                          style={{
                            position: 'absolute',
                            left: `${(trimEnd / duration) * 100}%`,
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '12px',
                            height: '20px',
                            backgroundColor: '#dc3545',
                            borderRadius: '2px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border: '2px solid white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                          }}
                          title={`End: ${formatTime(trimEnd)}`}
                          onClick={() => {
                            if (videoRef.current) {
                              videoRef.current.currentTime = trimEnd;
                              setCurrentTime(trimEnd);
                            }
                          }}
                        />
                        {/* Current position marker */}
                        <div
                          style={{
                            position: 'absolute',
                            left: `${(currentTime / duration) * 100}%`,
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '8px',
                            height: '24px',
                            backgroundColor: '#6c757d',
                            borderRadius: '50%'
                          }}
                        />
                      </div>
                      <div className="d-flex justify-content-between mt-1">
                        <small className="text-muted">0:00</small>
                        <small className="text-muted">{formatTime(duration)}</small>
                      </div>
                    </div>
                  )}
                  
                  {/* Current Time Slider with real-time preview */}
                  <div className="position-relative">
                    <Form.Range
                      value={currentTime}
                      min={0}
                      max={duration}
                      step={0.1}
                      onChange={(e) => {
                        const newTime = parseFloat(e.target.value);
                        setCurrentTime(newTime);
                        if (videoRef.current) {
                          videoRef.current.currentTime = newTime;
                        }
                      }}
                      onMouseDown={() => {
                        if (videoRef.current && isPlaying) {
                          videoRef.current.pause();
                        }
                      }}
                      onMouseUp={() => {
                        if (videoRef.current && isPlaying && !previewTrim) {
                          videoRef.current.play();
                        }
                      }}
                      disabled={processing}
                      style={{
                        background: isTrimMode 
                          ? `linear-gradient(to right, 
                              #dee2e6 0%, 
                              #dee2e6 ${(trimStart/duration)*100}%, 
                              ${previewTrim ? '#28a745' : '#ffc107'} ${(trimStart/duration)*100}%, 
                              ${previewTrim ? '#28a745' : '#ffc107'} ${(trimEnd/duration)*100}%, 
                              #dee2e6 ${(trimEnd/duration)*100}%, 
                              #dee2e6 100%)`
                          : '#dee2e6'
                      }}
                    />
                    {isTrimMode && (
                      <div className="d-flex justify-content-between mt-1">
                        <small className="text-primary font-weight-bold">{formatTime(trimStart)}</small>
                        <small className="text-muted">Current: {formatTime(currentTime)}</small>
                        <small className="text-danger font-weight-bold">{formatTime(trimEnd)}</small>
                      </div>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          {/* Editing Controls */}
          <Col md={4}>
            <Tabs defaultActiveKey="effects" className="mb-3">
              <Tab eventKey="effects" title="Effects">
                <Card>
                  <Card.Body>
                    <div className="mb-3">
                      <Form.Label>Presets</Form.Label>
                      <div className="d-flex flex-wrap gap-2">
                        {Object.entries({
                          normal: 'Normal',
                          vintage: 'Vintage',
                          blackWhite: 'B&W',
                          cinematic: 'Cinematic',
                          warm: 'Warm',
                          cool: 'Cool'
                        }).map(([key, label]) => (
                          <Button 
                            key={key}
                            size="sm" 
                            variant={effects === presetEffects[key] ? "primary" : "outline-secondary"}
                            onClick={() => applyPreset(key)}
                            style={{
                              transition: 'all 0.2s ease',
                              transform: effects === presetEffects[key] ? 'scale(1.05)' : 'scale(1)',
                              boxShadow: effects === presetEffects[key] ? '0 2px 8px rgba(0,123,255,0.4)' : 'none'
                            }}
                          >
                            {label}
                          </Button>
                        ))}
                      </div>
                    </div>
                    
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex justify-content-between">
                        <span>Brightness</span>
                        <Badge variant="info">{effects.brightness}%</Badge>
                      </Form.Label>
                      <Form.Range
                        min={0}
                        max={200}
                        value={effects.brightness}
                        onChange={(e) => setEffects({...effects, brightness: parseInt(e.target.value)})}
                        disabled={processing}
                        style={{
                          background: `linear-gradient(to right, #007bff 0%, #007bff ${effects.brightness/2}%, #dee2e6 ${effects.brightness/2}%, #dee2e6 100%)`
                        }}
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex justify-content-between">
                        <span>Contrast</span>
                        <Badge variant="info">{effects.contrast}%</Badge>
                      </Form.Label>
                      <Form.Range
                        min={0}
                        max={200}
                        value={effects.contrast}
                        onChange={(e) => setEffects({...effects, contrast: parseInt(e.target.value)})}
                        disabled={processing}
                        style={{
                          background: `linear-gradient(to right, #007bff 0%, #007bff ${effects.contrast/2}%, #dee2e6 ${effects.contrast/2}%, #dee2e6 100%)`
                        }}
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex justify-content-between">
                        <span>Saturation</span>
                        <Badge variant="info">{effects.saturation}%</Badge>
                      </Form.Label>
                      <Form.Range
                        min={0}
                        max={200}
                        value={effects.saturation}
                        onChange={(e) => setEffects({...effects, saturation: parseInt(e.target.value)})}
                        disabled={processing}
                        style={{
                          background: `linear-gradient(to right, #007bff 0%, #007bff ${effects.saturation/2}%, #dee2e6 ${effects.saturation/2}%, #dee2e6 100%)`
                        }}
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex justify-content-between">
                        <span>Blur</span>
                        <Badge variant="info">{effects.blur}px</Badge>
                      </Form.Label>
                      <Form.Range
                        min={0}
                        max={20}
                        value={effects.blur}
                        onChange={(e) => setEffects({...effects, blur: parseInt(e.target.value)})}
                        disabled={processing}
                        style={{
                          background: `linear-gradient(to right, #007bff 0%, #007bff ${effects.blur*5}%, #dee2e6 ${effects.blur*5}%, #dee2e6 100%)`
                        }}
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex justify-content-between">
                        <span>Grayscale</span>
                        <Badge variant="info">{effects.grayscale}%</Badge>
                      </Form.Label>
                      <Form.Range
                        min={0}
                        max={100}
                        value={effects.grayscale}
                        onChange={(e) => setEffects({...effects, grayscale: parseInt(e.target.value)})}
                        disabled={processing}
                        style={{
                          background: `linear-gradient(to right, #6c757d 0%, #6c757d ${effects.grayscale}%, #dee2e6 ${effects.grayscale}%, #dee2e6 100%)`
                        }}
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label className="d-flex justify-content-between">
                        <span>Sepia</span>
                        <Badge variant="info">{effects.sepia}%</Badge>
                      </Form.Label>
                      <Form.Range
                        min={0}
                        max={100}
                        value={effects.sepia}
                        onChange={(e) => setEffects({...effects, sepia: parseInt(e.target.value)})}
                        disabled={processing}
                        style={{
                          background: `linear-gradient(to right, #8B4513 0%, #8B4513 ${effects.sepia}%, #dee2e6 ${effects.sepia}%, #dee2e6 100%)`
                        }}
                      />
                    </Form.Group>
                    
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      onClick={resetEffects}
                      disabled={processing}
                      className="w-100"
                    >
                      Reset Effects
                    </Button>
                  </Card.Body>
                </Card>
              </Tab>
              
              <Tab eventKey="text" title="Text">
                <Card>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="text-overlay-switch"
                        label="Enable Text Overlay"
                        checked={textOverlay.enabled}
                        onChange={(e) => setTextOverlay({...textOverlay, enabled: e.target.checked})}
                        disabled={processing}
                      />
                    </Form.Group>
                    
                    {textOverlay.enabled && (
                      <>
                        <Form.Group className="mb-3">
                          <Form.Label>Text Content</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter text..."
                            value={textOverlay.text}
                            onChange={(e) => setTextOverlay({...textOverlay, text: e.target.value})}
                            disabled={processing}
                          />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                          <Form.Label>Position X: {textOverlay.x}%</Form.Label>
                          <Form.Range
                            min={0}
                            max={100}
                            value={textOverlay.x}
                            onChange={(e) => setTextOverlay({...textOverlay, x: parseInt(e.target.value)})}
                            disabled={processing}
                          />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                          <Form.Label>Position Y: {textOverlay.y}%</Form.Label>
                          <Form.Range
                            min={0}
                            max={100}
                            value={textOverlay.y}
                            onChange={(e) => setTextOverlay({...textOverlay, y: parseInt(e.target.value)})}
                            disabled={processing}
                          />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                          <Form.Label>Font Size: {textOverlay.fontSize}px</Form.Label>
                          <Form.Range
                            min={12}
                            max={72}
                            value={textOverlay.fontSize}
                            onChange={(e) => setTextOverlay({...textOverlay, fontSize: parseInt(e.target.value)})}
                            disabled={processing}
                          />
                        </Form.Group>
                        
                        <Row>
                          <Col>
                            <Form.Group className="mb-3">
                              <Form.Label>Text Color</Form.Label>
                              <Form.Control
                                type="color"
                                value={textOverlay.color}
                                onChange={(e) => setTextOverlay({...textOverlay, color: e.target.value})}
                                disabled={processing}
                              />
                            </Form.Group>
                          </Col>
                          <Col>
                          <Form.Group className="mb-3">
                            <Form.Label>Background</Form.Label>
                            <Form.Control
                              type="color"
                              value={(function() {
                                // Convert rgba(r,g,b,a) to hex
                                const rgba = textOverlay.backgroundColor;
                                const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
                                if (match) {
                                  const r = parseInt(match[1]).toString(16).padStart(2, '0');
                                  const g = parseInt(match[2]).toString(16).padStart(2, '0');
                                  const b = parseInt(match[3]).toString(16).padStart(2, '0');
                                  return `#${r}${g}${b}`;
                                }
                                return '#000000';
                              })()}
                              onChange={(e) => {
                                const hex = e.target.value;
                                const r = parseInt(hex.slice(1, 3), 16);
                                const g = parseInt(hex.slice(3, 5), 16);
                                const b = parseInt(hex.slice(5, 7), 16);
                                setTextOverlay({...textOverlay, backgroundColor: `rgba(${r},${g},${b},0.5)`});
                              }}
                              disabled={processing}
                            />
                          </Form.Group>

                          </Col>
                        </Row>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Tab>
              
              <Tab eventKey="audio" title="Audio">
                <Card>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Volume: {audioSettings.volume}%</Form.Label>
                      <Form.Range
                        min={0}
                        max={200}
                        value={audioSettings.volume}
                        onChange={(e) => setAudioSettings({...audioSettings, volume: parseInt(e.target.value)})}
                        disabled={processing || audioSettings.removeAudio}
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="mute-switch"
                        label="Mute Audio"
                        checked={audioSettings.muted}
                        onChange={(e) => setAudioSettings({...audioSettings, muted: e.target.checked})}
                        disabled={processing || audioSettings.removeAudio}
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="remove-audio-switch"
                        label="Remove Audio Track"
                        checked={audioSettings.removeAudio}
                        onChange={(e) => setAudioSettings({...audioSettings, removeAudio: e.target.checked})}
                        disabled={processing}
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Tab>
              
              <Tab eventKey="captions" title="Captions">
                <Card>
                  <Card.Body>
                    <div className="text-center py-4">
                      <i className="fas fa-closed-captioning fa-3x text-muted mb-3"></i>
                      <h6>Manage Captions</h6>
                      <p className="text-muted small">
                        Add, edit, or remove caption tracks for this video.
                      </p>
                      <Button 
                        variant="outline-primary" 
                        onClick={() => setShowCaptionModal(true)}
                      >
                        <i className="fas fa-cog me-2"></i>
                        Open Caption Manager
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Tab>
              
              <Tab eventKey="output" title="Output">
                <Card>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Output Format</Form.Label>
                      <Form.Select
                        value={outputFormat}
                        onChange={(e) => setOutputFormat(e.target.value)}
                        disabled={processing}
                      >
                        <option value="mp4">MP4 (H.264)</option>
                        <option value="webm">WebM (VP9)</option>
                        <option value="mov">MOV (QuickTime)</option>
                        <option value="avi">AVI</option>
                        <option value="mkv">MKV</option>
                      </Form.Select>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Quality</Form.Label>
                      <Form.Select
                        value={quality}
                        onChange={(e) => setQuality(e.target.value)}
                        disabled={processing}
                      >
                        <option value="high">High (Original)</option>
                        <option value="medium">Medium (720p)</option>
                        <option value="low">Low (480p)</option>
                        <option value="mobile">Mobile (360p)</option>
                      </Form.Select>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Resolution</Form.Label>
                      <Form.Select
                        value={resolution}
                        onChange={(e) => setResolution(e.target.value)}
                        disabled={processing}
                      >
                        <option value="original">Original</option>
                        <option value="1080p">1080p (1920x1080)</option>
                        <option value="720p">720p (1280x720)</option>
                        <option value="480p">480p (854x480)</option>
                        <option value="360p">360p (640x360)</option>
                      </Form.Select>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          </Col>
        </Row>
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={processing}>
          Cancel
        </Button>
        <Button 
          variant="outline-info" 
          onClick={() => setShowCaptionModal(true)}
          disabled={processing}
          className="me-2"
        >
          <i className="fas fa-closed-captioning mr-2"></i>
          Captions
        </Button>
        <Button 
          variant="outline-primary" 
          onClick={handleDownload}
          disabled={processing}
        >
          <i className="flaticon-download mr-2"></i>
          Download
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSave}
          disabled={processing}
        >
          <i className="flaticon-save mr-2"></i>
          Save Changes
        </Button>
      </Modal.Footer>
      
      {/* Caption Manager Modal */}
      <CaptionManager
        show={showCaptionModal}
        onHide={() => setShowCaptionModal(false)}
        itemId={asset?.id}
        itemType="vod"
      />
    </Modal>
  );
};

export default VideoEditor;
